import React, { useCallback, useEffect, useState, useRef, createElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { CustomCursor } from '../components/CustomCursor';
import { usePortfolio } from '../context/PortfolioContext';
import { ArrowRightIcon, CommandIcon, BriefcaseIcon, UploadCloudIcon, Loader2Icon, ZapIcon, LightbulbIcon, BarChart3Icon, CheckCircleIcon, AlertCircleIcon, FileTextIcon, XIcon, SparklesIcon, LinkIcon, ImageIcon, SearchIcon, FileIcon, ArrowLeftIcon, InfoIcon } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { analyzeJobDescription, analyzeJobDescriptionImage, analyzeJobDescriptionFromUrl, searchPortfolio } from '../utils/openaiService';
import { getTopMatchingCaseStudy, searchCaseStudies } from '../utils/caseStudyKeywords';
import { NoMatchModal } from '../components/NoMatchModal';
// Define the structure for our analyzed results
interface AnalysisResult {
  relevantCaseStudies: {
    slug: string;
    title: string;
    relevance: number;
    reason: string;
  }[];
  relevantSkills: {
    skill: string;
    category: string;
    relevance: number;
    description: string;
  }[];
  summary: string;
  jobTitle: string;
  companyName?: string;
  keyRequirements: string[];
  extractedKeywords?: string[];
  matchedRequirements?: {
    requirement: string;
    matchedWith: string;
  }[];
}
enum ViewState {
  PROMPT = 'prompt',
  PROCESSING = 'processing',
  RESULTS = 'results',
}
export function PromptHomePage() {
  const [prompt, setPrompt] = useState('');
  const [activeTab, setActiveTab] = useState('featured');
  const [isLoading, setIsLoading] = useState(false);
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const [viewState, setViewState] = useState<ViewState>(ViewState.PROMPT);
  const [isJobMode, setIsJobMode] = useState(false);
  const [jobResult, setJobResult] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [searchContext, setSearchContext] = useState(''); // NEW: Store what user is searching for
  const [showLinkedInWarning, setShowLinkedInWarning] = useState(false); // NEW: LinkedIn URL warning
  const loadingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const promptInputRef = useRef(null);
  const navigate = useNavigate();
  const [showNoMatchModal, setShowNoMatchModal] = useState(false);
  const [noMatchSearchTerm, setNoMatchSearchTerm] = useState('');
  const {
    userType,
    setViewPreference
  } = usePortfolio();
  const [searchResult, setSearchResult] = useState<any>(null);
  // Set view preference to 'ai' when this page loads
  useEffect(() => {
    setViewPreference('ai');
  }, [setViewPreference]);
  // Focus the prompt input when the component mounts
  useEffect(() => {
    if (promptInputRef.current) {
      promptInputRef.current.focus();
    }
  }, []);
  // Load UnicornStudio script
  useEffect(() => {
    if (!window.UnicornStudio) {
      window.UnicornStudio = {
        isInitialized: false
      };
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/gh/hiunicornstudio/unicornstudio.js@v1.4.29/dist/unicornStudio.umd.js';
      script.onload = () => {
        if (!window.UnicornStudio.isInitialized) {
          window.UnicornStudio.init();
          window.UnicornStudio.isInitialized = true;
        }
      };
      (document.head || document.body).appendChild(script);
    }
  }, []);
  const handlePromptChange = e => {
    const value = e.target.value;
    setPrompt(value);
    // Check if user is pasting a LinkedIn job URL
    if (isJobMode && value.includes('linkedin.com/jobs')) {
      setShowLinkedInWarning(true);
    } else {
      setShowLinkedInWarning(false);
    }
    if (value.length > 0) {
      setShowPlaceholder(false);
    } else {
      setShowPlaceholder(true);
    }
  };
  const handlePromptSubmit = async e => {
    e.preventDefault();
    const trimmedPrompt = prompt.trim();
    if (trimmedPrompt === '') return;
    console.log('🚀 Form submitted');
    console.log('📝 Input value:', trimmedPrompt);
    console.log('🎯 Mode:', isJobMode ? 'JOB MATCH' : 'SEARCH');
    // If we're in job mode, analyze the job description
    if (isJobMode) {
      console.log('✅ Routing to job analysis');
      await analyzeJobPrompt(trimmedPrompt);
      return;
    }
    // Otherwise, use AI search to find relevant case study
    console.log('✅ Routing to portfolio search');
    setIsLoading(true);
    setViewState(ViewState.PROCESSING);
    setSearchContext(trimmedPrompt);
    try {
      const result = await searchPortfolio(trimmedPrompt);
      setSearchResult(result);
      if (result.type === 'case-study' && result.caseStudy) {
        setTimeout(() => {
          navigate(`/case-study/${result.caseStudy.slug}`);
        }, 1500);
      } else if (result.type === 'skill' || result.type === 'general') {
        setViewState(ViewState.RESULTS);
        setIsLoading(false);
      } else {
        setError('No matching projects found. Try a different search.');
        setViewState(ViewState.PROMPT);
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Search error:', error);
      setError('Search failed. Please try again.');
      setViewState(ViewState.PROMPT);
      setIsLoading(false);
    }
  };
  const handleSuggestedTopic = (topic: string) => {
    setPrompt(topic);
    setShowPlaceholder(false);
    setShowNoMatchModal(false);
  };
  const analyzeJobPrompt = async jobDescription => {
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('🎯 JOB ANALYSIS STARTED');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('📝 Input length:', jobDescription.length);
    console.log('📝 First 200 chars:', jobDescription.substring(0, 200));
    if (jobDescription.trim() === '') {
      setError('Please enter a job description or upload a file');
      return;
    }
    setError('');
    setIsLoading(true);
    setViewState(ViewState.PROCESSING);
    startProgressSimulation();
    try {
      // Smart detection: Check if input is a URL
      const trimmedInput = jobDescription.trim();
      let analysisResult: AnalysisResult;
      // Check for URL
      if (trimmedInput.startsWith('http://') || trimmedInput.startsWith('https://')) {
        console.log('🔗 INPUT TYPE: URL');
        console.log('🔗 URL:', trimmedInput);
        console.log('📤 Calling analyzeJobDescriptionFromUrl...');
        analysisResult = await analyzeJobDescriptionFromUrl(trimmedInput);
      }
      // Check for very short input (might be a mistake)
      else if (trimmedInput.length < 50) {
        console.log('⚠️ INPUT TYPE: Very short text (< 50 chars)');
        console.log('⚠️ This might not be a complete job description');
        console.log('📤 Proceeding with text analysis anyway...');
        analysisResult = await analyzeJobDescription(trimmedInput);
      }
      // Regular text input
      else {
        console.log('📝 INPUT TYPE: Text');
        console.log('📤 Calling analyzeJobDescription...');
        analysisResult = await analyzeJobDescription(trimmedInput);
      }
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('✅ ANALYSIS COMPLETE');
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('📊 Job Title:', analysisResult.jobTitle);
      console.log('🏢 Company:', analysisResult.companyName || 'Not specified');
      console.log('🔑 Keywords:', analysisResult.extractedKeywords?.length || 0);
      console.log('💼 Skills:', analysisResult.relevantSkills?.length || 0);
      console.log('📁 Case Studies:', analysisResult.relevantCaseStudies?.length || 0);
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      setJobResult(analysisResult);
      setIsLoading(false);
      stopProgressSimulation();
      setLoadingProgress(100);
      setTimeout(() => {
        setViewState(ViewState.RESULTS);
      }, 500);
    } catch (err) {
      console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.error('❌ ANALYSIS FAILED');
      console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.error('Error:', err);
      console.error('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      setError('Error analyzing job description. Please try again.');
      setIsLoading(false);
      stopProgressSimulation();
      setViewState(ViewState.PROMPT);
    }
  };
  const startProgressSimulation = () => {
    setLoadingProgress(0);
    loadingIntervalRef.current = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev < 30) return prev + 2;
        if (prev < 60) return prev + 1;
        if (prev < 90) return prev + 0.5;
        return prev;
      });
    }, 100);
  };
  const stopProgressSimulation = () => {
    if (loadingIntervalRef.current) {
      clearInterval(loadingIntervalRef.current);
      loadingIntervalRef.current = null;
    }
  };
  const handleTabChange = tab => {
    setActiveTab(tab);
  };
  const toggleJobMode = () => {
    setIsJobMode(!isJobMode);
    setPrompt('');
    setShowPlaceholder(true);
    setViewState(ViewState.PROMPT);
    setJobResult(null);
    setError('');
    setUploadedFile(null);
  };
  const handleJobSuggestion = suggestion => {
    setPrompt(suggestion);
    setShowPlaceholder(false);
    analyzeJobPrompt(suggestion);
  };
  const resetForm = () => {
    setPrompt('');
    setUploadedFile(null);
    setShowPlaceholder(true);
    setJobResult(null);
    setError('');
    setViewState(ViewState.PROMPT);
  };
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    const file = acceptedFiles[0];
    setUploadedFile(file);
    setError('');
    setViewState(ViewState.PROCESSING);
    startProgressSimulation();
    try {
      // Check if file is an image
      const isImage = file.type.startsWith('image/');
      if (isImage) {
        console.log('📸 Processing image file:', file.name);
        // Use image analysis for image files
        try {
          const analysisResult = await analyzeJobDescriptionImage(file);
          setJobResult(analysisResult);
          stopProgressSimulation();
          setLoadingProgress(100);
          setTimeout(() => {
            setViewState(ViewState.RESULTS);
          }, 500);
        } catch (analysisErr) {
          console.error('Error analyzing image:', analysisErr);
          setError('Error analyzing the image. Please try again with a clearer image or paste the text instead.');
          stopProgressSimulation();
          setViewState(ViewState.PROMPT);
        }
      } else {
        // Handle text files (PDF, TXT, DOCX)
        console.log('📄 Processing text file:', file.name);
        const text = await readFileAsText(file);
        setPrompt(text);
        setShowPlaceholder(false);
        try {
          const analysisResult = await analyzeJobDescription(text);
          // Set source type to 'file' for uploaded text files
          analysisResult.sourceType = 'file';
          analysisResult.sourceDetails = file.name;
          setJobResult(analysisResult);
          stopProgressSimulation();
          setLoadingProgress(100);
          setTimeout(() => {
            setViewState(ViewState.RESULTS);
          }, 500);
        } catch (analysisErr) {
          console.error('Error analyzing file content:', analysisErr);
          setError('Error analyzing the file content. Please try again.');
          stopProgressSimulation();
          setViewState(ViewState.PROMPT);
        }
      }
    } catch (err) {
      console.error('Error reading file:', err);
      setError(`Error reading file. Please try again.`);
      stopProgressSimulation();
      setViewState(ViewState.PROMPT);
    }
  }, []);
  const {
    getRootProps,
    getInputProps,
    isDragActive
  } = useDropzone({
    onDrop,
    accept: {
      'text/plain': ['.txt'],
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/webp': ['.webp']
    }
  });
  const readFileAsText = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = e => {
        if (e.target?.result) {
          const fileText = e.target.result as string;
          if (file.type !== 'text/plain') {
            resolve(`[Note: This is a ${file.type} file. Text extraction may be incomplete.]\n\n${fileText}`);
          } else {
            resolve(fileText);
          }
        } else {
          reject(new Error('Failed to read file'));
        }
      };
      reader.onerror = () => reject(reader.error || new Error('File reading failed'));
      reader.readAsText(file);
    });
  };
  const removeFile = () => {
    setUploadedFile(null);
  };
  return <div className="w-full min-h-screen bg-black text-white" data-id="element-1309">
      <CustomCursor data-id="element-1310" />
      <Header data-id="element-1311" />

      <div className="fixed bottom-5 left-5 z-50" data-id="element-1312">
        <button onClick={() => navigate('/portfolio/traditional')} className="h-10 px-4 rounded-full flex items-center justify-center gap-2
              shadow-lg transition-all duration-300 
              bg-white/10 backdrop-blur-md text-white 
              border border-white/20 hover:bg-white/20 font-switzer" aria-label="Switch to traditional portfolio view" data-id="element-1313">
          <SparklesIcon className="w-4 h-4" aria-hidden="true" data-id="element-1314" />
          <span className="text-sm font-medium" data-id="element-1315">AI Mode: ON</span>
        </button>
      </div>

      <section className="pt-32 pb-20 px-4 sm:px-6 md:px-8 relative overflow-hidden" data-id="element-1316">
        {/* UnicornStudio Background - Updated Project ID */}
        <div className="aura-background-component top-0 w-full -z-10 absolute h-full" data-id="element-1317">
          <div data-us-project="4gq2Yrv2p0bIa0hdLPQx" className="absolute w-full h-full left-0 top-0 -z-10" data-id="element-1318"></div>
        </div>

        <div className="max-w-4xl mx-auto relative z-10" data-id="element-1319">
          <div className="mb-12 text-center" data-id="element-1320">
            <h1 className="text-5xl sm:text-6xl md:text-7xl mb-6 text-white uppercase" style={{
            fontFamily: "'Gefika', sans-serif",
            fontWeight: 400,
            letterSpacing: '0.03em'
          }} data-id="element-1321">
              AI-FOLIO
            </h1>
            <p className="text-lg sm:text-xl text-white/70 max-w-2xl mx-auto leading-relaxed" style={{
            fontFamily: "'Ronzino', sans-serif",
            fontWeight: 400
          }} data-id="element-1322">
              Quickly view my skills curated to your needs. Search for specific
              expertise or paste a job description to see if I'm a match.
            </p>
          </div>
          {viewState === ViewState.PROMPT && <div data-id="element-1323">
              {/* Two buttons above search bar */}
              <div className="flex gap-3 mb-4 items-center" data-id="element-1324">
                <button onClick={() => setIsJobMode(false)} className={`relative group px-4 py-2 rounded-lg border transition-all ${!isJobMode ? 'bg-white text-black border-white' : 'bg-white/5 text-white border-white/20 hover:bg-white/10'}`} data-id="element-1325">
                  <div className="flex items-center gap-2" data-id="element-1326">
                    <SearchIcon className="w-4 h-4" data-id="element-1327" />
                    <span className="text-sm font-medium" data-id="element-1328">Search</span>
                  </div>
                </button>

                <button onClick={() => setIsJobMode(true)} className={`relative px-4 py-2 rounded-lg border transition-all ${isJobMode ? 'bg-white text-black border-white' : 'bg-white/5 text-white border-white/20 hover:bg-white/10'}`} data-id="element-1329">
                  <div className="flex items-center gap-1" data-id="element-1330">
                    <FileIcon className="w-3 h-3" data-id="element-1331" />
                    <span className="text-xs font-medium" data-id="element-1332">Role Match</span>
                  </div>
                </button>

                {/* InfoIcon - positioned to the right of Role Match button */}
                <div className="relative group" data-id="element-1333">
                  <InfoIcon className="w-4 h-4 text-white/50 hover:text-white/80 transition-colors cursor-help" data-id="element-1334" />
                  {/* Tooltip - positioned to the right */}
                  <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-50 w-64" data-id="element-1335">
                    <div className="bg-white text-black text-xs px-4 py-3 rounded-lg shadow-xl border border-gray-200" data-id="element-1336">
                      <p className="leading-relaxed" data-id="element-1337">
                        Have a specific role in mind? Paste the job requirements
                        here, and my AI will scan my entire portfolio and curate
                        all relevant experience, projects, and skills that match
                        your needs.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Search input area */}
              <form onSubmit={handlePromptSubmit} className="mb-4" data-id="element-1338">
                <div className="relative" data-id="element-1339">
                  <div className="flex items-center bg-white/5 border border-white/20 rounded-lg p-4 focus-within:border-white/40 transition-colors" data-id="element-1340">
                    {isJobMode ? <div className="flex items-center gap-2 mr-3" data-id="element-1341">
                        <div {...getRootProps()} className="cursor-pointer relative group" data-id="element-1342">
                          <input {...getInputProps()} aria-label="Upload job description file or image" className="sr-only" data-id="element-1343" />
                          {uploadedFile ? <div className="relative" data-id="element-1344">
                              <FileTextIcon className="h-5 w-5 text-blue-400" data-id="element-1345" />
                              <button type="button" onClick={e => {
                        e.stopPropagation();
                        removeFile();
                      }} className="absolute -top-1 -right-1 bg-black rounded-full p-0.5" data-id="element-1346">
                                <XIcon className="h-3 w-3" data-id="element-1347" />
                              </button>
                            </div> : isDragActive ? <UploadCloudIcon className="h-5 w-5 text-white/70 animate-pulse" data-id="element-1348" /> : <>
                              <UploadCloudIcon className="h-5 w-5 text-white/50 hover:text-white/80 transition-colors" data-id="element-1349" />
                              <div className="absolute opacity-0 group-hover:opacity-100 transition-opacity duration-200 bottom-full mb-2 left-1/2 -translate-x-1/2 bg-white text-black text-xs px-3 py-1.5 rounded-lg whitespace-nowrap z-10 shadow-lg" data-id="element-1350">
                                Upload PDF or screenshot
                              </div>
                            </>}
                        </div>
                        <div className="h-5 w-px bg-white/20" data-id="element-1351"></div>
                      </div> : <CommandIcon className="h-5 w-5 text-white/50 mr-3" data-id="element-1352" />}
                    {isJobMode ? <input ref={promptInputRef} type="text" value={prompt} onChange={handlePromptChange} className="flex-grow bg-transparent text-white outline-none placeholder-white/30" placeholder="Paste job description text or public job URL (Greenhouse, Lever, etc.)" disabled={isLoading} data-id="element-1353" /> : <input ref={promptInputRef} type="text" value={prompt} onChange={handlePromptChange} className="flex-grow bg-transparent text-white outline-none placeholder-white/30" placeholder="Search projects or ask about skills..." disabled={isLoading} data-id="element-1354" />}
                    <button type="submit" className="ml-2 p-2 rounded-md bg-white/10 hover:bg-white/20 transition-colors disabled:opacity-50" disabled={isLoading || !prompt.trim()} data-id="element-1355">
                      {isLoading ? <div className="h-5 w-5 border-2 border-white/50 border-t-white rounded-full animate-spin" data-id="element-1356"></div> : <ArrowRightIcon className="h-5 w-5 text-white" data-id="element-1357" />}
                    </button>
                  </div>

                  {/* LinkedIn Warning Message */}
                  {showLinkedInWarning && <div className="mt-3 bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4" data-id="element-1358">
                      <div className="flex items-start gap-3" data-id="element-1359">
                        <AlertCircleIcon className="h-5 w-5 text-yellow-400 mt-0.5 flex-shrink-0" data-id="element-1360" />
                        <div data-id="element-1361">
                          <p className="text-yellow-200 text-sm font-medium mb-2" data-id="element-1362">
                            LinkedIn job links require authentication
                          </p>
                          <p className="text-yellow-200/80 text-xs mb-3" data-id="element-1363">
                            I can't access LinkedIn job postings directly. For
                            best results:
                          </p>
                          <div className="space-y-1.5 text-xs text-yellow-200/80" data-id="element-1364">
                            <div className="flex items-center gap-2" data-id="element-1365">
                              <CheckCircleIcon className="h-3.5 w-3.5 text-yellow-400" data-id="element-1366" />
                              <span data-id="element-1367">
                                Take a screenshot of the job posting and upload
                                it
                              </span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-1368">
                              <CheckCircleIcon className="h-3.5 w-3.5 text-yellow-400" data-id="element-1369" />
                              <span data-id="element-1370">
                                Copy and paste the job description text
                              </span>
                            </div>
                            <div className="flex items-center gap-2" data-id="element-1371">
                              <CheckCircleIcon className="h-3.5 w-3.5 text-yellow-400" data-id="element-1372" />
                              <span data-id="element-1373">
                                Use public job board URLs (Greenhouse, Lever,
                                Workday)
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>}

                  {error && <div className="flex items-center text-red-400 text-sm mt-2" data-id="element-1374">
                      <AlertCircleIcon className="h-4 w-4 mr-2" data-id="element-1375" />
                      {error}
                    </div>}
                </div>
              </form>

              {/* Helper text below search bar - Updated for Role Match */}
              {isJobMode && !showLinkedInWarning && <div className="mb-8 text-center" data-id="element-1376">
                  <p className="text-white/50 text-xs" data-id="element-1377">
                    💡 Best results: Upload PDF/screenshot or paste full job
                    description text
                  </p>
                </div>}

              {/* File upload display */}
              {isJobMode && uploadedFile && <div className="bg-white/5 border border-white/20 rounded-lg p-4 flex items-center justify-between mb-8" data-id="element-1378">
                  <div className="flex items-center" data-id="element-1379">
                    <FileTextIcon className="h-5 w-5 text-white/50 mr-3" data-id="element-1380" />
                    <div data-id="element-1381">
                      <p className="text-white font-medium" data-id="element-1382">
                        {uploadedFile.name}
                      </p>
                      <p className="text-white/50 text-xs" data-id="element-1383">
                        {(uploadedFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <button type="button" onClick={removeFile} className="text-white/70 hover:text-white" data-id="element-1384">
                    <XIcon className="h-4 w-4" data-id="element-1385" />
                  </button>
                </div>}
              {/* Suggestions - only show in Search mode */}
              {!isJobMode && <div className="mb-8" data-id="element-1386">
                  <h3 className="text-sm uppercase text-white/50 mb-4" data-id="element-1387">
                    Quick prompts:
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1388">
                    <button onClick={async () => {
                const query = 'Do you have financial product design experience?';
                setPrompt(query);
                setShowPlaceholder(false);
                setSearchContext(query);
                setIsLoading(true);
                setViewState(ViewState.PROCESSING);
                try {
                  const result = await searchPortfolio(query);
                  if (result.type === 'case-study' && result.caseStudy) {
                    setTimeout(() => {
                      navigate(`/case-study/${result.caseStudy.slug}`);
                    }, 1500);
                  }
                } catch (error) {
                  console.error('Search error:', error);
                  setError('Search failed. Please try again.');
                  setViewState(ViewState.PROMPT);
                  setIsLoading(false);
                }
              }} className="bg-white/5 border border-white/10 rounded-md px-4 py-3 text-sm text-left hover:bg-white/10 transition-colors" data-id="element-1389">
                      "Do you have financial product design experience?"
                    </button>
                    <button onClick={async () => {
                const query = 'Any experience designing data heavy screens?';
                setPrompt(query);
                setShowPlaceholder(false);
                setSearchContext(query);
                setIsLoading(true);
                setViewState(ViewState.PROCESSING);
                try {
                  const result = await searchPortfolio(query);
                  if (result.type === 'case-study' && result.caseStudy) {
                    setTimeout(() => {
                      navigate(`/case-study/${result.caseStudy.slug}`);
                    }, 1500);
                  }
                } catch (error) {
                  console.error('Search error:', error);
                  setError('Search failed. Please try again.');
                  setViewState(ViewState.PROMPT);
                  setIsLoading(false);
                }
              }} className="bg-white/5 border border-white/10 rounded-md px-4 py-3 text-sm text-left hover:bg-white/10 transition-colors" data-id="element-1390">
                      "Any experience designing data heavy screens?"
                    </button>
                  </div>
                </div>}
            </div>}
          {/* Processing/Loading screen */}
          {viewState === ViewState.PROCESSING && <div className="max-w-xl mx-auto text-center" data-id="element-1391">
              <div className="mb-8" data-id="element-1392">
                <div className="inline-flex items-center justify-center p-4 bg-white/5 rounded-full mb-6" data-id="element-1393">
                  <Loader2Icon className="h-10 w-10 text-white animate-spin" data-id="element-1394" />
                </div>
                <h2 className="text-xl sm:text-2xl mb-3" style={{
              fontFamily: "'Gefika', sans-serif",
              fontWeight: 400
            }} data-id="element-1395">
                  {isJobMode ? 'Analyzing Job Description' : 'Scanning Portfolio'}
                </h2>
                <p className="text-white/70 text-lg mb-2" data-id="element-1396">
                  {isJobMode ? 'AI is scanning my entire portfolio and resume to match your requirements with relevant case studies, skills, and experience...' : searchContext ? `AI is searching through all case studies for projects matching "${searchContext}"` : 'AI is scanning through portfolio for the best match...'}
                </p>
                {!isJobMode && searchResult?.caseStudy && <p className="text-white/50 text-sm mt-4" data-id="element-1397">
                    Found: {searchResult.caseStudy.title}
                  </p>}
              </div>
              {isJobMode && <>
                  <div className="w-full bg-white/5 rounded-full h-2 mb-8" data-id="element-1398">
                    <div className="bg-white h-2 rounded-full transition-all duration-300 ease-out" style={{
                width: `${loadingProgress}%`
              }} data-id="element-1399"></div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-1400">
                    <div className="bg-white/5 border border-white/10 rounded-lg p-4" data-id="element-1401">
                      <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center mb-3 mx-auto" data-id="element-1402">
                        <span className="text-sm font-medium" data-id="element-1403">1</span>
                      </div>
                      <p className="text-sm text-white/70" data-id="element-1404">
                        Scanning all case studies
                      </p>
                    </div>
                    <div className="bg-white/5 border border-white/10 rounded-lg p-4" data-id="element-1405">
                      <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center mb-3 mx-auto" data-id="element-1406">
                        <span className="text-sm font-medium" data-id="element-1407">2</span>
                      </div>
                      <p className="text-sm text-white/70" data-id="element-1408">
                        Matching skills & experience
                      </p>
                    </div>
                    <div className="bg-white/5 border border-white/10 rounded-lg p-4" data-id="element-1409">
                      <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center mb-3 mx-auto" data-id="element-1410">
                        <span className="text-sm font-medium" data-id="element-1411">3</span>
                      </div>
                      <p className="text-sm text-white/70" data-id="element-1412">
                        Curating relevant projects
                      </p>
                    </div>
                  </div>
                </>}
            </div>}
          {/* Results section */}
          {viewState === ViewState.RESULTS && jobResult && <div className="max-w-4xl mx-auto" data-id="element-1413">
              {/* Back Button - NEW */}
              <div className="mb-8" data-id="element-1414">
                <button onClick={resetForm} className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors group" aria-label="Back to job matcher" data-id="element-1415">
                  <ArrowLeftIcon className="w-4 h-4 transition-transform group-hover:-translate-x-1" data-id="element-1416" />
                  <span className="text-sm font-medium" data-id="element-1417">
                    Back to Job Matcher
                  </span>
                </button>
              </div>

              {/* Extracted Job Information Section - NEW */}
              <div className="mb-8" data-id="element-1418">
                <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-6" data-id="element-1419">
                  <div className="flex items-start mb-4" data-id="element-1420">
                    <FileTextIcon className="h-5 w-5 text-blue-400 mt-1 mr-3 flex-shrink-0" data-id="element-1421" />
                    <div className="flex-grow" data-id="element-1422">
                      <h3 className="text-lg font-semibold text-blue-300 mb-2" data-id="element-1423">
                        Job Description Analysis
                      </h3>

                      {/* Source Information - NEW */}
                      {jobResult.sourceType && <div className="mb-4 pb-4 border-b border-blue-500/20" data-id="element-1424">
                          <div className="flex items-center gap-2 text-sm text-blue-200/80" data-id="element-1425">
                            {jobResult.sourceType === 'url' && <>
                                <LinkIcon className="w-4 h-4" data-id="element-1426" />
                                <span data-id="element-1427">Analyzed from URL:</span>
                              </>}
                            {jobResult.sourceType === 'image' && <>
                                <ImageIcon className="w-4 h-4" data-id="element-1428" />
                                <span data-id="element-1429">Analyzed from image:</span>
                              </>}
                            {jobResult.sourceType === 'file' && <>
                                <FileIcon className="w-4 h-4" data-id="element-1430" />
                                <span data-id="element-1431">Analyzed from file:</span>
                              </>}
                            {jobResult.sourceType === 'text' && <>
                                <FileTextIcon className="w-4 h-4" data-id="element-1432" />
                                <span data-id="element-1433">Analyzed from pasted text</span>
                              </>}
                          </div>
                          {jobResult.sourceDetails && <p className="text-xs text-blue-200/60 mt-1 break-all" data-id="element-1434">
                              {jobResult.sourceDetails}
                            </p>}
                        </div>}

                      <div className="space-y-3" data-id="element-1435">
                        <div data-id="element-1436">
                          <span className="text-white/50 text-sm" data-id="element-1437">
                            Position:
                          </span>
                          <p className="text-white font-medium text-lg" data-id="element-1438">
                            {jobResult.jobTitle}
                          </p>
                        </div>
                        {jobResult.companyName && <div data-id="element-1439">
                            <span className="text-white/50 text-sm" data-id="element-1440">
                              Company:
                            </span>
                            <p className="text-white font-medium" data-id="element-1441">
                              {jobResult.companyName}
                            </p>
                          </div>}
                        {jobResult.extractedKeywords && jobResult.extractedKeywords.length > 0 && <div data-id="element-1442">
                              <span className="text-white/50 text-sm" data-id="element-1443">
                                Key Terms Found:
                              </span>
                              <div className="flex flex-wrap gap-2 mt-2" data-id="element-1444">
                                {jobResult.extractedKeywords.map((keyword, index) => <span key={index} className="bg-blue-500/20 text-blue-200 px-3 py-1 rounded-full text-xs border border-blue-500/30" data-id="element-1445">
                                      {keyword}
                                    </span>)}
                              </div>
                            </div>}
                      </div>
                    </div>
                  </div>
                  <p className="text-white/60 text-sm italic" data-id="element-1446">
                    ✓ Successfully analyzed your job description and matched it
                    with relevant experience
                  </p>
                </div>
              </div>

              {/* Matched Requirements Section - NEW */}
              {jobResult.matchedRequirements && jobResult.matchedRequirements.length > 0 && <div className="mb-16" data-id="element-1447">
                    <h2 className="text-2xl sm:text-3xl mb-6 flex items-center uppercase" style={{
              fontFamily: "'Gefika', sans-serif",
              fontWeight: 400,
              letterSpacing: '0.02em'
            }} data-id="element-1448">
                      <CheckCircleIcon className="h-6 w-6 mr-3 text-green-400" data-id="element-1449" />
                      How I Match Your Requirements
                    </h2>
                    <div className="space-y-4" data-id="element-1450">
                      {jobResult.matchedRequirements.map((match, index) => <div key={index} className="bg-white/5 border border-white/20 rounded-lg p-5 hover:border-white/40 transition-colors" data-id="element-1451">
                          <div className="flex items-start gap-4" data-id="element-1452">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center" data-id="element-1453">
                              <CheckCircleIcon className="h-5 w-5 text-green-400" data-id="element-1454" />
                            </div>
                            <div className="flex-grow" data-id="element-1455">
                              <p className="text-white font-medium mb-2" data-id="element-1456">
                                "{match.requirement}"
                              </p>
                              <p className="text-white/70 text-sm" data-id="element-1457">
                                → {match.matchedWith}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </div>}

              {/* Summary section */}
              <div className="mb-16" data-id="element-1458">
                <div className="bg-white/5 border border-white/20 rounded-lg p-8" data-id="element-1459">
                  <div className="flex items-center mb-6" data-id="element-1460">
                    <div className="bg-white/10 p-3 rounded-full mr-4" data-id="element-1461">
                      <BarChart3Icon className="h-6 w-6 text-white" data-id="element-1462" />
                    </div>
                    <div data-id="element-1463">
                      <h2 className="text-2xl uppercase" style={{
                    fontFamily: "'Gefika', sans-serif",
                    fontWeight: 400,
                    letterSpacing: '0.02em'
                  }} data-id="element-1464">
                        {jobResult.jobTitle} Match Analysis
                      </h2>
                      <p className="text-white/70" data-id="element-1465">
                        Here's how my experience aligns with this role
                      </p>
                    </div>
                  </div>
                  <p className="text-lg leading-relaxed mb-8" data-id="element-1466">
                    {jobResult.summary}
                  </p>
                  <div className="mb-8" data-id="element-1467">
                    <h3 className="text-lg font-semibold mb-4" data-id="element-1468">
                      Key Requirements
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-1469">
                      {jobResult.keyRequirements.map((req, index) => <div key={index} className="flex items-start" data-id="element-1470">
                          <CheckCircleIcon className="h-5 w-5 text-green-400 mt-0.5 mr-2 flex-shrink-0" data-id="element-1471" />
                          <span data-id="element-1472">{req}</span>
                        </div>)}
                    </div>
                  </div>
                  <button onClick={resetForm} className="text-white/70 hover:text-white text-sm flex items-center transition-colors" data-id="element-1473">
                    Analyze another job description
                    <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-1474" />
                  </button>
                </div>
              </div>
              {/* Relevant skills section */}
              <div className="mb-16" data-id="element-1475">
                <h2 className="text-2xl sm:text-3xl mb-8 flex items-center uppercase" style={{
              fontFamily: "'Gefika', sans-serif",
              fontWeight: 400,
              letterSpacing: '0.02em'
            }} data-id="element-1476">
                  <ZapIcon className="h-6 w-6 mr-3 text-yellow-400" data-id="element-1477" />
                  Matching Skills
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1478">
                  {jobResult.relevantSkills.map((skill, index) => <div key={index} className="bg-white/5 border border-white/20 rounded-lg p-6 hover:border-white/40 transition-colors" data-id="element-1479">
                      <div className="flex justify-between items-start mb-3" data-id="element-1480">
                        <h3 className="text-xl font-semibold" data-id="element-1481">{skill.skill}</h3>
                        <span className="bg-white/10 px-2 py-1 rounded text-sm" data-id="element-1482">
                          {Math.round(skill.relevance * 100)}% match
                        </span>
                      </div>
                      <p className="text-white/70 mb-2" data-id="element-1483">{skill.description}</p>
                      <span className="text-xs uppercase tracking-wider text-white/50" data-id="element-1484">
                        {skill.category}
                      </span>
                    </div>)}
                </div>
              </div>
              {/* Relevant case studies section */}
              <div data-id="element-1485">
                <h2 className="text-2xl sm:text-3xl mb-8 flex items-center uppercase" style={{
              fontFamily: "'Gefika', sans-serif",
              fontWeight: 400,
              letterSpacing: '0.02em'
            }} data-id="element-1486">
                  <LightbulbIcon className="h-6 w-6 mr-3 text-blue-400" data-id="element-1487" />
                  Relevant Case Studies
                </h2>
                <div className="space-y-8" data-id="element-1488">
                  {jobResult.relevantCaseStudies.map((study, index) => <div key={index} className="border border-white/20 rounded-lg overflow-hidden bg-white/5" data-id="element-1489">
                      <div className="p-6" data-id="element-1490">
                        <div className="flex flex-col md:flex-row md:items-start gap-6" data-id="element-1491">
                          <div className="md:w-1/3 h-48 overflow-hidden rounded-lg" data-id="element-1492">
                            <img src={study.slug === 'us-bank' ? '/usbthumb.png' : study.slug === 'cyber-security' ? '/unisys_thumb.png' : '/Gemini_Generated_Image_qq6ptsqq6ptsqq6p.png'} alt={study.title} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" data-id="element-1493" />
                          </div>
                          <div className="md:w-2/3" data-id="element-1494">
                            <div className="flex justify-between items-start mb-4" data-id="element-1495">
                              <h3 className="text-xl font-semibold" data-id="element-1496">
                                {study.title}
                              </h3>
                              <span className="bg-white/10 px-2 py-1 rounded text-sm" data-id="element-1497">
                                {Math.round(study.relevance * 100)}% relevant
                              </span>
                            </div>
                            <div className="mb-4" data-id="element-1498">
                              <p className="text-white/70 mb-4" data-id="element-1499">
                                {study.reason}
                              </p>
                              <div className="bg-white/10 p-4 rounded-lg mb-4 border-l-4 border-blue-400" data-id="element-1500">
                                <p className="text-white/90 text-sm italic" data-id="element-1501">
                                  {study.slug === 'us-bank' && 'A look at how I improved customer satisfaction and team productivity on a Corporate Payment Services team focused on the One Card application at U.S. Bank.'}
                                  {study.slug === 'cyber-security' && 'My first test as a designer on a large platform re-design for a cybersecurity project. I helped transform complex micro segmentation and threat analysis applications into intuitive, user-centered solutions.'}
                                  {study.slug === 'ai-comparison' && 'During our initial research, we explored the capabilities that would be required for an effective conversational AI solution. We conducted a comprehensive analysis of conversational AI platforms, evaluating offerings from IBM Watson, Google Dialogflow, Microsoft, and Amazon.'}
                                </p>
                              </div>
                              <div className="mb-4" data-id="element-1502">
                                <h4 className="text-sm uppercase text-white/50 mb-2" data-id="element-1503">
                                  Key Skills Demonstrated:
                                </h4>
                                <div className="flex flex-wrap gap-2" data-id="element-1504">
                                  {study.slug === 'us-bank' && <>
                                      <span className="bg-blue-900/50 text-blue-100 px-2 py-1 rounded text-xs" data-id="element-1505">
                                        UX Design
                                      </span>
                                      <span className="bg-purple-900/50 text-purple-100 px-2 py-1 rounded text-xs" data-id="element-1506">
                                        Financial Services
                                      </span>
                                      <span className="bg-green-900/50 text-green-100 px-2 py-1 rounded text-xs" data-id="element-1507">
                                        Customer Experience
                                      </span>
                                    </>}
                                  {study.slug === 'cyber-security' && <>
                                      <span className="bg-blue-900/50 text-blue-100 px-2 py-1 rounded text-xs" data-id="element-1508">
                                        UI Design
                                      </span>
                                      <span className="bg-red-900/50 text-red-100 px-2 py-1 rounded text-xs" data-id="element-1509">
                                        Technical Communication
                                      </span>
                                      <span className="bg-yellow-900/50 text-yellow-100 px-2 py-1 rounded text-xs" data-id="element-1510">
                                        Complex System Simplification
                                      </span>
                                    </>}
                                  {study.slug === 'ai-comparison' && <>
                                      <span className="bg-blue-900/50 text-blue-100 px-2 py-1 rounded text-xs" data-id="element-1511">
                                        AI Strategy
                                      </span>
                                      <span className="bg-indigo-900/50 text-indigo-100 px-2 py-1 rounded text-xs" data-id="element-1512">
                                        Conversational Design
                                      </span>
                                      <span className="bg-teal-900/50 text-teal-100 px-2 py-1 rounded text-xs" data-id="element-1513">
                                        Platform Evaluation
                                      </span>
                                    </>}
                                </div>
                              </div>
                            </div>
                            <Link to={`/case-study/${study.slug}`} className="inline-flex items-center text-white font-medium text-sm hover:text-blue-300 transition-colors" data-id="element-1514">
                              View Full Case Study
                              <ArrowRightIcon className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-1515" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>}
          {/* Browse My Work Section */}
          {(viewState !== ViewState.RESULTS || !isJobMode) && <>
              <div id="my-work" className="mb-8 mt-16" data-id="element-1516">
                <h2 className="text-2xl sm:text-3xl mb-2 uppercase" style={{
              fontFamily: "'Gefika', sans-serif",
              fontWeight: 400,
              letterSpacing: '0.03em'
            }} data-id="element-1517">
                  My Work
                </h2>
                <p className="text-white/60 mb-6" style={{
              fontFamily: "'Ronzino', sans-serif",
              fontWeight: 400
            }} data-id="element-1518">
                  Explore featured projects and case studies
                </p>
              </div>
              <div className="flex border-b border-white/20 mb-8" role="tablist" aria-label="Project categories" data-id="element-1519">
                <button onClick={() => handleTabChange('featured')} role="tab" aria-selected={activeTab === 'featured'} aria-controls="featured-panel" className={`px-4 py-2 text-sm font-medium ${activeTab === 'featured' ? 'text-white border-b-2 border-white' : 'text-white/50 hover:text-white/80'}`} data-id="element-1520">
                  Featured Work
                </button>
                <button onClick={() => handleTabChange('case-studies')} role="tab" aria-selected={activeTab === 'case-studies'} aria-controls="case-studies-panel" className={`px-4 py-2 text-sm font-medium ${activeTab === 'case-studies' ? 'text-white border-b-2 border-white' : 'text-white/50 hover:text-white/80'}`} data-id="element-1521">
                  Case Studies
                </button>
              </div>
              <div className="mt-8" data-id="element-1522">
                {activeTab === 'featured' && <div id="featured-panel" role="tabpanel" aria-labelledby="featured-tab" className="grid grid-cols-1 gap-8" data-id="element-1523">
                    <Link to="/case-study/case-western" className="group block relative overflow-hidden rounded-lg transition-all duration-300 hover:shadow-lg" onMouseEnter={() => window.cursorSelectProject && window.cursorSelectProject()} onMouseLeave={() => window.cursorDefault && window.cursorDefault()} aria-label="View Case Western Reserve University project" data-id="element-1524">
                      <div className="relative aspect-w-4 aspect-h-3" data-id="element-1525">
                        <img src="https://cdn.magicpatterns.com/uploads/r6QmYhNhTgdAoSYhnocL3u/CWRU_COMPV.png" alt="Case Western Reserve University Faculty Information System interface" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-1526" />
                        <div className="absolute inset-0 bg-black/60 group-hover:bg-black/70 transition-colors" data-id="element-1527"></div>
                        <div className="absolute inset-0 flex flex-col justify-center items-center text-center p-6" data-id="element-1528">
                          <p className="text-sm uppercase tracking-wider text-white/70 mb-3" data-id="element-1529">
                            Featured Work
                          </p>
                          <h3 className="text-2xl sm:text-3xl font-bold text-white mb-6" data-id="element-1530">
                            CWRU AI-Powered FIS System
                          </h3>
                          <span className="inline-flex items-center text-white font-medium group-hover:underline" data-id="element-1531">
                            View Project
                            <svg className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true" data-id="element-1532">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" data-id="element-1533" />
                            </svg>
                          </span>
                        </div>
                      </div>
                    </Link>
                  </div>}
                {activeTab === 'case-studies' && <div id="case-studies-panel" role="tabpanel" aria-labelledby="case-studies-tab" className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1534">
                    <Link to="/case-study/us-bank" className="group block relative overflow-hidden rounded-lg transition-all duration-300 hover:shadow-lg" onMouseEnter={() => window.cursorSelectProject && window.cursorSelectProject()} onMouseLeave={() => window.cursorDefault && window.cursorDefault()} aria-label="View US Bank case study" data-id="element-1535">
                      <div className="relative aspect-w-4 aspect-h-3" data-id="element-1536">
                        <img src="/usbthumb.png" alt="US Bank Corporate Payment Services interface" className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110" data-id="element-1537" />
                        <div className="absolute inset-0 bg-black/60 group-hover:bg-black/70 transition-colors" data-id="element-1538"></div>
                        <div className="absolute inset-0 flex flex-col justify-center items-center text-center p-4" data-id="element-1539">
                          <p className="text-xs uppercase tracking-wider text-white/70 mb-1" data-id="element-1540">
                            FINANCIAL SERVICES
                          </p>
                          <h3 className="text-xl font-bold text-white mb-2" data-id="element-1541">
                            US Bank
                          </h3>
                          <span className="inline-flex items-center text-white text-sm font-medium group-hover:underline" data-id="element-1542">
                            View Case Study
                          </span>
                        </div>
                      </div>
                    </Link>
                    <Link to="/case-study/cyber-security" className="group block relative overflow-hidden rounded-lg transition-all duration-300 hover:shadow-lg" onMouseEnter={() => window.cursorSelectProject && window.cursorSelectProject()} onMouseLeave={() => window.cursorDefault && window.cursorDefault()} aria-label="View Cyber Security case study" data-id="element-1543">
                      <div className="relative aspect-w-4 aspect-h-3" data-id="element-1544">
                        <img src="/unisys_thumb.png" alt="Cybersecurity platform interface showing threat analysis" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-1545" />
                        <div className="absolute inset-0 bg-black/60 group-hover:bg-black/70 transition-colors" data-id="element-1546"></div>
                        <div className="absolute inset-0 flex flex-col justify-center items-center text-center p-4" data-id="element-1547">
                          <p className="text-xs uppercase tracking-wider text-white/70 mb-1" data-id="element-1548">
                            DIGITAL PROTECTION
                          </p>
                          <h3 className="text-xl font-bold text-white mb-2" data-id="element-1549">
                            Cyber Security
                          </h3>
                          <span className="inline-flex items-center text-white text-sm font-medium group-hover:underline" data-id="element-1550">
                            View Case Study
                          </span>
                        </div>
                      </div>
                    </Link>
                    <Link to="/case-study/ai-comparison" className="group block relative overflow-hidden rounded-lg transition-all duration-300 hover:shadow-lg" onMouseEnter={() => window.cursorSelectProject && window.cursorSelectProject()} onMouseLeave={() => window.cursorDefault && window.cursorDefault()} aria-label="View AI Comparison case study" data-id="element-1551">
                      <div className="relative aspect-w-4 aspect-h-3" data-id="element-1552">
                        <img src="/Gemini_Generated_Image_qq6ptsqq6ptsqq6p.png" alt="AI conversational interface comparison visualization" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-1553" />
                        <div className="absolute inset-0 bg-black/60 group-hover:bg-black/70 transition-colors" data-id="element-1554"></div>
                        <div className="absolute inset-0 flex flex-col justify-center items-center text-center p-4" data-id="element-1555">
                          <p className="text-xs uppercase tracking-wider text-white/70 mb-1" data-id="element-1556">
                            AI COMPARISON
                          </p>
                          <h3 className="text-xl font-bold text-white mb-2" data-id="element-1557">
                            Watson vs Dialogflow
                          </h3>
                          <span className="inline-flex items-center text-white text-sm font-medium group-hover:underline" data-id="element-1558">
                            View Case Study
                          </span>
                        </div>
                      </div>
                    </Link>
                  </div>}
              </div>
            </>}
        </div>
      </section>
      <Footer data-id="element-1559" />
      <NoMatchModal isOpen={showNoMatchModal} onClose={() => setShowNoMatchModal(false)} searchTerm={noMatchSearchTerm} onSuggestedTopicClick={handleSuggestedTopic} data-id="element-1560" />
    </div>;
}